import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { FramePanel } from "@/components/ui/frame";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import {
  ArrowRightIcon,
  BrainCircuit,
  FileQuestionIcon,
  Workflow,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import createDOMPurify from "dompurify";
import { useMemo } from "react";
import {
  useBriefStore,
  useCampaignStore,
  useOrganizationSettingsStore,
} from "@/stores";
import { useAuth, useUser } from "@clerk/react";
import EmptyState from "@/components/ui/empty-state";
import { useState, useEffect } from "react";
import { BRIEF_SECTIONS } from "@/constants/brief-sections";
import posthog from "posthog-js";
import { MinimalTiptapEditor } from "@/components/minimal-tiptap";
import AmyBadge from "@/components/ui/amy-badge";
import {
  SwitchCard,
  SwitchCardSwitch,
  SwitchCardContent,
  SwitchCardLabel,
  SwitchCardDescription,
} from "@/components/ui/switch-card";

interface StartCampaignProps {
  allCreatorsPending: boolean;
  outreachLoading: boolean;
  handleStartInitialOutreach: (options: { refreshCreatorData: boolean }) => void;
  campaignId: string;
}

export default function StartCampaign({
  allCreatorsPending,
  outreachLoading,
  handleStartInitialOutreach,
  campaignId,
}: StartCampaignProps) {
  const DOMPurify = useMemo(() => createDOMPurify(window), []);
  const { brief, isLoading } = useBriefStore();
  // Amy-run campaigns launch through Amy (no send on click) — see
  // CreatorsTable.handleStartInitialOutreach.
  const { campaign } = useCampaignStore();
  const isAmyRun = campaign?.is_eve === true;
  // "Refresh creator data first": re-scrape followers/views, then launch.
  // On by default for every campaign.
  const [refreshCreatorData, setRefreshCreatorData] = useState(true);
  const { getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const { organizationInfo, fetchOrganizationInfo } =
    useOrganizationSettingsStore();
  const FAQ = organizationInfo?.faq || "";

  const fetchLatestOrganizationInfo = async () => {
    try {
      const token = await getToken();
      if (token && user) {
        fetchOrganizationInfo(campaignId, user.id, token);
      }
    } catch (error) {
      console.error("Error fetching organization info:", error);
    }
  };

  const [hasEmptyBriefSections, setHasEmptyBriefSections] = useState(() => {
    if (!brief) return true;

    if (brief.mode === "freeform") {
      return !brief.brief_content || brief.brief_content === "";
    }

    // For guided mode - only check required fields
    return BRIEF_SECTIONS.some((section) => {
      if (!section.required) return false;

      const sectionValue = brief[section.id as keyof typeof brief];
      return !sectionValue || sectionValue === "";
    });
  });

  useEffect(() => {
    if (!brief) {
      setHasEmptyBriefSections(true);
      return;
    }

    if (brief.mode === "freeform") {
      setHasEmptyBriefSections(
        !brief.brief_content || brief.brief_content === ""
      );
      return;
    }

    // For guided mode
    const isEmpty = BRIEF_SECTIONS.some((section) => {
      const sectionValue = brief[section.id as keyof typeof brief];

      return !sectionValue || sectionValue === "";
    });

    setHasEmptyBriefSections(isEmpty);
  }, [brief]);

  return (
    <AlertDialog>
      <AlertDialogTrigger
        asChild
        disabled={allCreatorsPending || outreachLoading || isLoading}
        onClick={fetchLatestOrganizationInfo}
        className="mx-1.5 ml-auto self-center"
      >
        <Button size="sm">
          {outreachLoading && <Spinner />}
          {!outreachLoading && <Workflow />}
          Prepare Launch
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-3xl overflow-auto">
        {!FAQ && (
          <AlertDialogHeader>
            <AlertDialogTitle>Before we start the outreach</AlertDialogTitle>
            <AlertDialogDescription>
              Amy is needs to learn more about your brand. Go to settings and
              train amy to create a custom FAQ based on your brand's needs.
            </AlertDialogDescription>
          </AlertDialogHeader>
        )}

        {FAQ && hasEmptyBriefSections && (
          <AlertDialogHeader>
            <AlertDialogTitle className="flex flex-row items-center gap-1">
              <AmyBadge imgClassName="size-7.5" className="p-0.5 pr-1.5 gap-1.5" />
              <span>needs the brief!</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Create a brief for Amy to use as context.
            </AlertDialogDescription>
          </AlertDialogHeader>
        )}

        {FAQ && !hasEmptyBriefSections && (
          <AlertDialogHeader>
            <AlertDialogTitle className="flex flex-row items-center gap-1">
              <AmyBadge imgClassName="size-7.5" className="p-0.5 pr-1.5 gap-1.5" />
              <span>is ready for outreach!</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              She'll begin with the first 3 creators. Then operate according to the {" "}
              <Button
                variant="link"
                className="p-1 h-auto group transition-all text-sm"
                scope={false}
                onClick={() =>
                  navigate(`../settings/email`)
                }
              >
                Campaign Schedule
                <ArrowRightIcon className="group-hover:translate-x-0.5 transition-all" />
              </Button>
            </AlertDialogDescription>
          </AlertDialogHeader>
        )}

        {!hasEmptyBriefSections && !allCreatorsPending && (
          <>
            {brief?.mode === "freeform" && (
              <FramePanel className="p-0">
                <ScrollArea scrollFade className="flex max-h-96 flex-col rounded-[inherit] [&_[data-slot=scroll-area-viewport]>div]:!block">
                  <MinimalTiptapEditor
                    content={DOMPurify.sanitize(brief?.brief_content || "")}
                    className="prose prose-sm max-w-none bg-transparent p-5 text-sm w-auto border-none shadow-none"
                    editorContentClassName="bg-transparent"
                    showToolBar={false}
                    editable={false}
                  />
                </ScrollArea>
              </FramePanel>
            )}
            {brief?.mode === "guided" && (
              <FramePanel className="p-0">
                {/* Long briefs scroll inside the panel, faded at the edges. */}
                <ScrollArea scrollFade className="flex max-h-96 flex-col rounded-[inherit] [&_[data-slot=scroll-area-viewport]>div]:!block">
                  <div className="divide-y">
                    {BRIEF_SECTIONS.map((section, index) => (
                      <div key={section.id} className="flex flex-col gap-1 px-4 py-3">
                        <span className="text-sm font-medium">
                          {index + 1}. {section.title}
                        </span>
                        <p className="whitespace-pre-wrap wrap-break-word text-sm text-muted-foreground">
                          {String(brief?.[section.id as keyof typeof brief] || "—")}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </FramePanel>
            )}
          </>
        )}

        {FAQ && hasEmptyBriefSections && (
          <div className="w-full h-full flex items-center justify-center pb-5">
            <EmptyState icon={<FileQuestionIcon />}>
              <Button
                variant="secondary"
                className="group transition-all"
                onClick={() => {
                  posthog.capture("start_campaign_no_brief", {
                    campaignId,
                  });
                  navigate(`../brief`);
                }}
              >
                Go to Brief tab
                <ArrowRightIcon className="group-hover:translate-x-0.25 transition-all" />
              </Button>
            </EmptyState>
          </div>
        )}

        {isAmyRun && FAQ && !hasEmptyBriefSections && (
          <p className="text-muted-foreground px-1 text-xs">
            Amy runs this campaign: launching queues the creators for her, and
            she starts emailing
            {campaign?.eve_mailboxes?.some((m) => !m.paused)
              ? ` from ${campaign.eve_mailboxes
                  .filter((m) => !m.paused)
                  .map((m) => m.email ?? m.account_id)
                  .join(", ")}`
              : ""}{" "}
            during the campaign&apos;s active hours. Nothing is sent the
            moment you click.
          </p>
        )}

        {FAQ && !hasEmptyBriefSections && (
          <SwitchCard variant="ghost" size="sm" disabled={outreachLoading}>
            <SwitchCardSwitch
              id="refresh-creator-data"
              checked={refreshCreatorData}
              onCheckedChange={setRefreshCreatorData}
              aria-describedby="refresh-creator-data-description"
            />
            <SwitchCardContent>
              <SwitchCardLabel htmlFor="refresh-creator-data">
                Refresh creator data first
              </SwitchCardLabel>
              <SwitchCardDescription id="refresh-creator-data-description">
                Re-scrapes followers/views, then launches. Skips creators updated within 7 days.
              </SwitchCardDescription>
            </SwitchCardContent>
          </SwitchCard>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction className="p-0 border-none">
            {!FAQ && (
              <Button
                onClick={() => navigate("/dashboard/settings")}
                className="w-full"
              >
                <BrainCircuit />
                Go Train Amy
              </Button>
            )}

            {FAQ && !hasEmptyBriefSections && (
              <Button
                onClick={() =>
                  handleStartInitialOutreach({ refreshCreatorData })
                }
                disabled={outreachLoading || hasEmptyBriefSections}
              >
                <Workflow />
                {isAmyRun ? "Launch with Amy" : "Start Outreach Sequence"}
              </Button>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
